import { Component } from '@theme/component';

/**
 * Drives the exploded stick from the page scroll.
 *
 * Publishes `--explode-progress` (0 = stick closed, 1 = pulled apart with the
 * powder out) on itself; the section's CSS does the rest. Progress follows the
 * stage's position in the viewport, so scrolling back up closes the stick
 * again. Without this script, and for visitors who prefer reduced motion, the
 * CSS default of 1 shows the finished, opened stick.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage - The element the animation plays in.
 *
 * @extends {Component<Refs>}
 */
class ExplodedStickComponent extends Component {
  requiredRefs = ['stage'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #scrollController;

  #frame = 0;

  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    if (this.#reducedMotion.matches) return;

    // Scroll work only happens while the stage is near the viewport
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) this.#listen();
        else this.#unlisten();
      },
      { rootMargin: '25% 0px' }
    );
    this.#observer.observe(this.refs.stage);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#unlisten();
  }

  #listen() {
    if (this.#scrollController) return;

    this.#scrollController = new AbortController();
    const { signal } = this.#scrollController;

    // Captured at the document: the page scrolls inside `.page-wrapper` on
    // desktop and on the window on phones, and scroll events do not bubble
    document.addEventListener('scroll', this.#schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#update();
  }

  #unlisten() {
    this.#scrollController?.abort();
    this.#scrollController = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #schedule = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const { top, height } = this.refs.stage.getBoundingClientRect();
    const viewport = window.innerHeight;

    // Follows the tear itself, halfway down the stage: still closed as it
    // comes into view near the bottom of the screen, fully open once it
    // reaches the middle. Until then the top of the stick reads as intact.
    const tear = top + height / 2;
    const start = viewport * 0.92;
    const end = viewport * 0.5;
    const linear = Math.min(1, Math.max(0, (start - tear) / (start - end)));

    // Ease in-out: a beat of tension, a quick burst, then it settles
    const progress = linear < 0.5 ? 4 * linear ** 3 : 1 - (-2 * linear + 2) ** 3 / 2;

    this.style.setProperty('--explode-progress', progress.toFixed(4));
  }
}

if (!customElements.get('exploded-stick-component')) {
  customElements.define('exploded-stick-component', ExplodedStickComponent);
}
